import { useState } from "react";
import { useParams } from "react-router-dom";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { TrashIcon } from "@heroicons/react/24/outline";
import { ApiTokenScope } from "shared";

import { Spinner } from "../../../Common/Spinner";
import { Button } from "../../../../../common/Atoms/Button";
import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { FormLabel } from "../../../../../common/Atoms/FormLabel";
import { TwoColFormRow } from "../../../Molecules/Form/TwoColFormRow";
import { useFetchApiTokensQuery, useRevokeApiTokenMutation } from "../../../../services/api/apiTokenApi/apiToken";

import { ApiTokenModal } from "./ApiTokenModal";
import { McpConnectorModal } from "./McpConnectorModal";

export function ApiTokens() {
  const { organisationId } = useParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [mcpModalOpen, setMcpModalOpen] = useState(false);

  const { data, isLoading } = useFetchApiTokensQuery(organisationId, {
    skip: !organisationId,
  });
  const [revokeApiToken, { isLoading: isRevoking }] = useRevokeApiTokenMutation();

  const tokens = data?.tokens ?? [];

  async function handleRevoke(tokenId: string) {
    try {
      await revokeApiToken({ organisationId, tokenId }).unwrap();
      toast.success(`API token revoked`);
    }
    catch {
      toast.error(`Could not revoke API token`);
    }
  }

  return (
    <TwoColFormRow
      heading={ `API Tokens` }
      description={ `Create tokens to authenticate with the Paidnice public API.` }
    >
      <div className={ `space-y-3` }>
        { isLoading ? (
          <Spinner />
        ) : (
          <>
            { tokens.length === 0 ? (
              <Paragraph variant={ `secondary` }>
                { `No API tokens yet.` }
              </Paragraph>
            ) : (
              <div>
                <div className={ `grid grid-cols-12 border-b border-gray-200 pb-1` }>
                  <FormLabel className={ `col-span-4` }>{ `Created` }</FormLabel>
                  <FormLabel className={ `col-span-4` }>{ `Created by` }</FormLabel>
                  <FormLabel className={ `col-span-3` }>{ `Access` }</FormLabel>
                </div>
                { tokens.map(token => (
                  <div
                    key={ token.id }
                    className={ `grid grid-cols-12 items-center border-b border-gray-100 py-2` }
                  >
                    <Paragraph
                      className={ `col-span-4` }
                      variant={ `secondary` }
                    >
                      { DateTime.fromISO(token.createdAt).toFormat(`yyyy-LL-dd HH:mm`) }
                    </Paragraph>
                    <Paragraph
                      className={ `col-span-4` }
                      variant={ `secondary` }
                    >
                      { token.createdBy.name || token.createdBy.email || `Unknown` }
                    </Paragraph>
                    <Paragraph
                      className={ `col-span-3` }
                      variant={ `secondary` }
                    >
                      { token.scopes.includes(ApiTokenScope.WRITE) ? `Read and write` : `Read only` }
                    </Paragraph>
                    <div className={ `col-span-1 flex justify-end` }>
                      <TrashIcon
                        className={ `h-5 w-5 text-paidniceRed-600 cursor-pointer` }
                        onClick={ () => !isRevoking && handleRevoke(token.id) }
                      />
                    </div>
                  </div>
                )) }
              </div>
            ) }

            <div className={ `flex space-x-3` }>
              <Button
                color={ `paidniceGreen` }
                onClick={ () => setModalOpen(true) }
              >
                { `Create new token` }
              </Button>
              <Button
                color={ `gray` }
                onClick={ () => setMcpModalOpen(true) }
              >
                { `Connect an agent (MCP)` }
              </Button>
            </div>
          </>
        ) }
      </div>

      <ApiTokenModal
        open={ modalOpen }
        setOpen={ setModalOpen }
        organisationId={ organisationId }
      />

      <McpConnectorModal
        open={ mcpModalOpen }
        setOpen={ setMcpModalOpen }
      />
    </TwoColFormRow>
  );
}
